import { clearStoredAuth, getSessionWithSync, supabase } from './supabase';
import { translateApiError } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const DISABLED_SUBSCRIPTION_STATUS_RESPONSE = {
  success: true,
  data: {
    subscription_status: 'active',
    cancel_at_period_end: false,
    plan_name: 'FakturaOne Pro',
    amount: 2900,
    currency: 'eur',
    active_count: 1,
    current_period_end: null,
  },
};

async function getAuthHeaders(baseHeaders = {}, body = null) {
  let token = localStorage.getItem('token');
  if (supabase) {
    try {
      const session = await getSessionWithSync();
      token = session?.access_token || token;
    } catch {}
  }
  const headers = { ...baseHeaders };
  const isFormDataBody = typeof FormData !== 'undefined' && body instanceof FormData;
  const hasContentType = Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');
  if (!isFormDataBody && !hasContentType) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function request(path, options = {}) {
  const { responseType, ...fetchOptions } = options;
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    ...fetchOptions,
    headers: await getAuthHeaders(fetchOptions.headers || {}, fetchOptions.body),
  });
  if (res.status === 401) {
    clearStoredAuth();
    window.location.href = '/login';
    throw new Error(translateApiError({ error: { code: 'UNAUTHORIZED' } }));
  }
  // For PDF/XML binary responses
  if (responseType === 'blob') {
    if (!res.ok) {
      let msg = translateApiError({ error: { code: 'UNKNOWN_ERROR' } });
      try {
        const errData = await res.json();
        msg = translateApiError(errData);
      } catch {}
      throw new Error(msg);
    }
    return res.blob();
  }
  const data = await res.json();
  if (!res.ok) {
    const msg = translateApiError(data);
    throw new Error(msg);
  }
  return data;
}

export const api = {
  // Auth
  devLogin: () => request('/api/auth/dev-login', { method: 'POST' }),
  me: () => request('/api/auth/me'),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  updateUser: (body) => request('/api/auth/user', { method: 'PATCH', body: JSON.stringify(body) }),
  changePassword: (body) => request('/api/auth/change-password', { method: 'POST', body: JSON.stringify(body) }),
  getAccountDeletionStatus: () => request('/api/auth/account-deletion/status'),
  requestAccountDeletion: () => request('/api/auth/account-deletion/request', { method: 'POST' }),
  confirmAccountDeletion: () => request('/api/auth/account-deletion/confirm', { method: 'POST' }),
  downloadAccountDeletionExport: () => request('/api/auth/account-deletion/export', { responseType: 'blob' }),

  // Subscription
  // Temporarily disabled: return static active status to bypass paywall checks.
  getSubscriptionStatus: async () => DISABLED_SUBSCRIPTION_STATUS_RESPONSE,
  createCheckout: (body) => request('/api/subscription/checkout', { method: 'POST', body: JSON.stringify(body) }),
  checkPaymentStatus: (sessionId) => request(`/api/subscription/check/${sessionId}`),
  cancelSubscription: () => request('/api/subscription/cancel', { method: 'POST' }),
  reactivateSubscription: () => request('/api/subscription/reactivate', { method: 'POST' }),
  getInvoices: () => request('/api/subscription/invoices'),
  createPortalSession: (body) => request('/api/subscription/portal', { method: 'POST', body: JSON.stringify(body) }),

  // Unternehmen
  getUnternehmen: () => request('/api/unternehmen'),
  createUnternehmen: (body) => request('/api/unternehmen', { method: 'POST', body: JSON.stringify(body) }),
  updateUnternehmen: (body) => request('/api/unternehmen', { method: 'PATCH', body: JSON.stringify(body) }),

  // Customizing
  getCustomizing: () => request('/api/customizing'),
  updateCustomizing: (body) => request('/api/customizing', { method: 'PATCH', body: JSON.stringify(body) }),

  // Kunden
  listKunden: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/kunden?${qs}`);
  },
  getKunde: (id) => request(`/api/kunden/${id}`),
  createKunde: (body) => request('/api/kunden', { method: 'POST', body: JSON.stringify(body) }),
  updateKunde: (id, body) => request(`/api/kunden/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteKunde: (id) => request(`/api/kunden/${id}`, { method: 'DELETE' }),

  // Produkte
  listProdukte: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/produkte?${qs}`);
  },
  getProdukt: (id) => request(`/api/produkte/${id}`),
  createProdukt: (body) => request('/api/produkte', { method: 'POST', body: JSON.stringify(body) }),
  updateProdukt: (id, body) => request(`/api/produkte/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteProdukt: (id) => request(`/api/produkte/${id}`, { method: 'DELETE' }),

  // Rechnungen
  listRechnungen: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/rechnungen?${qs}`);
  },
  getRechnung: (id) => request(`/api/rechnungen/${id}`),
  createRechnung: (body) => request('/api/rechnungen', { method: 'POST', body: JSON.stringify(body) }),
  updateRechnung: (id, body) => request(`/api/rechnungen/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  updateRechnungStatus: (id, status) => request(`/api/rechnungen/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  createInvoiceFromOffer: (id) => request(`/api/rechnungen/${id}/create-invoice`, { method: 'POST' }),
  createTeilRueckerstattung: (id, body) => request(`/api/rechnungen/${id}/teilrueckerstattung`, { method: 'POST', body: JSON.stringify(body) }),
  importRechnungenCsv: (file) => {
    const body = new FormData();
    body.append('file', file);
    return request('/api/rechnungen/import-csv', { method: 'POST', body });
  },
  importRechnungPdf: (formData) => request('/api/rechnungen/import-pdf', { method: 'POST', body: formData }),
  deleteRechnung: (id) => request(`/api/rechnungen/${id}`, { method: 'DELETE' }),

  // PDF + ZUGFeRD
  downloadPdf: (id) => request(`/api/rechnungen/${id}/pdf`, { responseType: 'blob' }),
  downloadDocumentPdf: (id, documentVariant) => request(`/api/rechnungen/${id}/pdf?document_variant=${encodeURIComponent(documentVariant)}`, { responseType: 'blob' }),
  downloadZugferd: (id) => request(`/api/rechnungen/${id}/zugferd`, { responseType: 'blob' }),
  previewInvoiceXml: (file) => {
    const body = new FormData();
    body.append('file', file);
    return request('/api/rechnungen/xml-vorschau', { method: 'POST', body });
  },

  // Eingangsrechnungen (MVP Inbox)
  uploadIncomingInvoice: (file) => {
    const body = new FormData();
    body.append('file', file);
    return request('/api/eingangsrechnungen', { method: 'POST', body });
  },
  listIncomingInvoices: (params = {}) => {
    const sanitized = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
    const qs = new URLSearchParams(sanitized).toString();
    return request(`/api/eingangsrechnungen${qs ? `?${qs}` : ''}`);
  },
  getIncomingInvoicesSummary: () => request('/api/eingangsrechnungen/summary'),
  getIncomingInvoice: (id) => request(`/api/eingangsrechnungen/${id}`),
  updateIncomingInvoiceStatus: (id, payload) =>
    request(`/api/eingangsrechnungen/${id}/status`, { method: 'PATCH', body: JSON.stringify(payload) }),
  downloadIncomingInvoiceXml: (id) => request(`/api/eingangsrechnungen/${id}/xml`, { responseType: 'blob' }),

  // Dokumente (Gen-Dokument)
  listDokumente: (id) => request(`/api/rechnungen/${id}/dokumente`),

  // Rechnungsnummer Vorschau
  rechnungsnummerVorschau: () => request('/api/rechnungen/nummern-vorschau'),

  // Dashboard
  dashboardStats: () => request('/api/dashboard/stats'),
  dashboardCollectionsActions: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/dashboard/collections-actions${qs ? `?${qs}` : ''}`);
  },
  completeCollectionsAction: (rechnungId, body = {}) =>
    request(`/api/dashboard/collections-actions/${rechnungId}/complete`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  snoozeCollectionsAction: (rechnungId, body) =>
    request(`/api/dashboard/collections-actions/${rechnungId}/snooze`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  setCollectionsReminderCooldown: (rechnungId, body) =>
    request(`/api/dashboard/collections-actions/${rechnungId}/reminder-cooldown`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  setCollectionsReminderMode: (rechnungId, body) =>
    request(`/api/dashboard/collections-actions/${rechnungId}/reminder-mode`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  sendCollectionsReminder: (rechnungId, body = {}) =>
    request(`/api/dashboard/collections-actions/${rechnungId}/reminder-send`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateUmsatzZiel: (body) => request('/api/dashboard/umsatzziel', { method: 'PATCH', body: JSON.stringify(body) }),
};
